"use client";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { ShippingMethod } from "./types";
import { formatPrice } from "@/lib/utils";
import { cn } from "@/lib/utils";

interface Props {
  itemId: string;
  shippingMethods: ShippingMethod[];
  selectedShippingMethod: ShippingMethod | null;
  currencyCode?: string;
  onSelectShippingAction: (itemId: string, shippingId: string) => void;
  onBeforeOpen?: () => void | Promise<void>;
  disabled?: boolean;
}

export function ShippingSelector({
  itemId,
  shippingMethods,
  selectedShippingMethod,
  currencyCode = "USD",
  onSelectShippingAction,
  onBeforeOpen,
  disabled = false,
}: Props & { disabled?: boolean }) {
  const t = useTranslations("sellerGroup");
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!open || !buttonRef.current) return;

    const updatePosition = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (rect) {
        setDropdownStyle({
          position: 'fixed',
          top: `${rect.bottom + 8}px`,
          left: `${rect.left}px`,
          width: '220px',
          maxWidth: '220px',
        });
      }
    };

    updatePosition();
    
    // Close dropdown immediately on any scroll or touch
    const scrollHandler = () => {
      setOpen(false);
    };
    
    // Listen to all scroll events (including in bottom sheet)
    window.addEventListener('scroll', scrollHandler, true);
    window.addEventListener('touchmove', scrollHandler, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', scrollHandler, true);
      window.removeEventListener('touchmove', scrollHandler, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current) return;
      if (!buttonRef.current) return;
      if (!containerRef.current.contains(event.target as Node) && 
          !buttonRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleSelect = (methodId: string) => {
    onSelectShippingAction(itemId, methodId);
    setOpen(false);
  };

  /* THEME-FOLLOWING since Phase 2 step 2f (2f-acceptance find, same call as
     `DROPDOWN_CHROME_SURFACE_CLASS`): this is `createPortal(…, document.body)`,
     so it escapes the cart panel's scope — which pre-2f was forced-dark and
     needed a matching `dark` here. Now the panel follows the site theme, the
     page theme IS the panel theme, and the portal resolves the right token set
     on its own; re-forcing `dark` would drop a dark menu out of a light sheet.
     @see .tondocs/ui/HANDOFF_SHEET_LIGHT_THEME_2026_08_01.md §7 + §4.1 */
  const dropdown = open && shippingMethods.length > 0 && mounted ? (
    <div
      ref={containerRef}
      className="z-[9999] rounded-2xl border p-1 shadow-2xl border-foreground/20 bg-muted/95 backdrop-blur-xs"
      style={dropdownStyle}
    >
      {shippingMethods.map((opt) => (
        <button
          key={opt.id}
          className="w-full rounded-xl px-3 py-2 text-left text-xs transition flex items-start justify-between text-foreground hover:bg-sheet-handle"
          onClick={(event) => {
            event.stopPropagation();
            if (!disabled) handleSelect(opt.id);
          }}
        >
          <span className="flex-1 wrap-break-word leading-snug pr-2 max-w-[140px] overflow-wrap-anywhere">
            {opt.name}
          </span>
          <span className="text-xs font-medium whitespace-nowrap shrink-0">
            {formatPrice(opt.price, currencyCode)}
          </span>
        </button>
      ))}
    </div>
  ) : null;

  return (
    <>
      <div className="relative w-full">
        <Button
          ref={buttonRef}
          variant="outline"
          size="sm"
          className={cn(
            "w-full justify-between",
            "h-8 min-h-8 px-2.5 py-1.5 rounded-xl text-xs border border-sheet-handle/50 bg-muted/80 text-foreground hover:bg-sheet-handle/80 hover:border-foreground/20 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-muted-foreground/70 focus-visible:ring-offset-1 focus-visible:ring-offset-sheet",
            disabled && "opacity-60 cursor-not-allowed animate-pulse"
          )}
          onClick={(event) => {
            event.stopPropagation();
            event.preventDefault();
            if (!disabled) {
              if (open) {
                setOpen(false);
                return;
              }
              setOpen(true);
              try {
                Promise.resolve(onBeforeOpen?.()).catch(() => {})
              } catch {}
            }
          }}
          onPointerDown={(event) => {
            event.stopPropagation();
          }}
          onFocus={(event) => {
            if (buttonRef.current) {
              buttonRef.current.blur();
            }
          }}
          disabled={disabled}
        >
          <span className={cn("flex-1 text-left truncate leading-tight pr-2 text-xs")}>
            {selectedShippingMethod
              ? `${selectedShippingMethod.name} (${formatPrice(selectedShippingMethod.price, currencyCode)})`
              : t("selectShipping")}
          </span>
          <ChevronDown className="shrink-0 h-3 w-3 text-muted-foreground/70" />
        </Button>
      </div>
      {mounted && dropdown && createPortal(dropdown, document.body)}
    </>
  );
}
